import { IPropertyService, iterateProperties, PropertyScope, PropertyViewMode, type IProperty } from "./properties.js";
import { normalizeIncomingPath } from "./propertyPath.js";
import { handleOf } from "../../webgl/common/objectHandle.js";

/**
 * The ONE path→property resolver every AI entry path shares. READS
 * (property_read / property_find) and WRITES (property.setValue) used to
 * disagree: reads walked the property collection, writes only did the address
 * lookup — so the read tools taught the model paths the write tool then
 * refused ("Property at path components/0/height not found"), and setting ONE
 * component field took six calls (live 2026-08-31).
 *
 * The raw object-path read stays read-only and lives in the read tool: a raw
 * value has no property to write through.
 */
export async function resolvePropertyByPath(
    propertyService: IPropertyService,
    object: object,
    path: string,
    handle: string | undefined,
): Promise<{ via: "address" | "property-walk"; property: IProperty } | null> {
    // 1. the property system's address lookup (hierarchy-addressed nodes)
    const rootHandle = handle ?? handleOf(object);
    if (rootHandle) {
        try {
            const property = await propertyService.findProperty({ objectId: rootHandle, object, propertyPath: path });
            if (property) return { via: "address", property };
        }
        catch { /* fall through to the walk */ }
    }

    // 2. a walk of the target's property collection: exact address, exact key,
    // or address suffix — tolerates path prefixes the model did not know
    try {
        const props = await propertyService.getProperties(object, {
            scope: PropertyScope.AI_QUERY,
            viewMode: PropertyViewMode.ALL,
        });
        if (props) {
            const wanted = normalizeIncomingPath(path);
            for (const group of props.groups) {
                for (const property of iterateProperties(group.properties)) {
                    const address = typeof property.metadata?.address === "string" ? property.metadata.address : undefined;
                    if (address === path || property.key === path
                        || (address && wanted && (address === wanted || address.endsWith(`/${wanted}`)))) {
                        return { via: "property-walk", property };
                    }
                }
            }
        }
    }
    catch { /* no collection for this object */ }

    return null;
}
